import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
  MapPin, MagnifyingGlass, Phone, EnvelopeSimple, Globe, Plus, Buildings, Target, Check,
  HardHat, AirplaneTilt, DownloadSimple,
} from "@phosphor-icons/react";

const RADII = [5, 10, 15, 20, 50];

export default function ProspectSearch() {
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [hotelId, setHotelId] = useState(sp.get("hotel") || "");
  const [radius, setRadius] = useState(10);
  const [customRadius, setCustomRadius] = useState("");
  const [industry, setIndustry] = useState("");
  const [mode, setMode] = useState("all"); // all | crew
  const [result, setResult] = useState(null);
  const [added, setAdded] = useState({}); // name -> true

  const { data: hotels = [] } = useQuery({
    queryKey: ["myHotels"], queryFn: () => api.hotels().then((all) => all),
  });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const myHotels = hotels.filter((h) => me && h.owner_id === (me.org_id || me.id));

  const search = useMutation({
    mutationFn: () => api.nearby(hotelId, Number(customRadius) || radius, mode),
    onSuccess: (d) => { setResult(d); setAdded({}); },
    onError: (e) => toast.error(errMsg(e, "Search failed")),
  });

  const createLead = useMutation({
    mutationFn: (c) => api.createLead({
      hotel_id: hotelId, company_name: c.name, email: c.email, phone: c.phone,
      address: c.address, industry: c.industry, website: c.website,
      category: mode === "crew" ? "Corporate/Crew" : "Corporate/Crew",
      city: result?.hotel?.city || "",
      distance_miles: c.distance_miles, source: c.source, lat: c.lat, lng: c.lng,
    }),
    onSuccess: (_, c) => {
      setAdded((a) => ({ ...a, [c.name]: true }));
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(`Lead created: ${c.name}`);
    },
    onError: (e) => toast.error(errMsg(e, "Could not create lead")),
  });

  const doSearch = () => {
    if (!hotelId) return toast.error("Select a property first");
    setResult(null);
    search.mutate();
  };

  const companies = (result?.companies || []).filter(
    (c) => !industry || (c.industry || "").toLowerCase().includes(industry.toLowerCase())
  );

  const exportCsv = () => {
    if (!companies.length) return;
    const rows = [["Name", "Industry", "Distance (mi)", "Phone", "Email", "Website", "Address", "Source"]];
    companies.forEach((c) => rows.push([
      c.name, c.industry || "", c.distance_miles, c.phone || "", c.email || "", c.website || "",
      (c.address || "").replace(/,/g, ";"), c.source,
    ]));
    const csv = rows.map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `prospects-${result?.hotel?.name || "list"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-center gap-2 text-[#4B5563] text-sm mb-2">
        <Target size={16} weight="bold" /> <span className="overline">Prospecting</span>
      </div>
      <h1 className="font-display font-black text-4xl text-[#111827]">Find companies near your hotel.</h1>
      <p className="text-[#4B5563] mt-2 max-w-xl">
        Search real businesses within a radius of a property, then push them into your sales
        pipeline as leads for your team to work.
      </p>

      <div className="flex gap-2 mt-6" data-testid="prospect-mode">
        <button data-testid="mode-all" onClick={() => setMode("all")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border transition-colors ${mode === "all" ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E5E7EB] text-[#4B5563] hover:border-[#111827]"}`}>
          <Buildings size={16} weight="bold" /> All companies
        </button>
        <button data-testid="mode-crew" onClick={() => setMode("crew")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border transition-colors ${mode === "crew" ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E5E7EB] text-[#4B5563] hover:border-[#111827]"}`}>
          <HardHat size={16} weight="bold" /> Sales Lead in Area
        </button>
      </div>
      {mode === "crew" && (
        <p className="text-xs text-[#047857] mt-2">Focused on crew-bringing companies — construction, energy, engineering, inspection, healthcare, manufacturing & logistics.</p>
      )}

      <div className="border border-[#111827] p-5 mt-4 grid md:grid-cols-12 gap-3 items-end">
        <label className="md:col-span-4 text-sm">
          <span className="overline text-[#4B5563] block mb-1">Property</span>
          <select data-testid="prospect-hotel" value={hotelId} onChange={(e) => setHotelId(e.target.value)}
            className="w-full border border-[#E5E7EB] px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            <option value="">Select a property…</option>
            {myHotels.map((h) => <option key={h.id} value={h.id}>{h.name} — {h.city}, {h.state}</option>)}
          </select>
        </label>
        <div className="md:col-span-4 text-sm">
          <span className="overline text-[#4B5563] block mb-1">Radius (miles)</span>
          <div className="flex gap-1.5 flex-wrap">
            {RADII.map((r) => (
              <button key={r} data-testid={`radius-${r}`} onClick={() => { setRadius(r); setCustomRadius(""); }}
                className={`px-3 py-2 text-sm border transition-colors ${!customRadius && radius === r ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E5E7EB] text-[#4B5563] hover:border-[#111827]"}`}>
                {r}
              </button>
            ))}
            <input data-testid="radius-custom" type="number" min="1" placeholder="Custom" value={customRadius}
              onChange={(e) => setCustomRadius(e.target.value)}
              className="w-20 px-2 py-2 text-sm border border-[#E5E7EB] focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
        </div>
        <label className="md:col-span-2 text-sm">
          <span className="overline text-[#4B5563] block mb-1">Industry filter</span>
          <input data-testid="prospect-industry" value={industry} onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g. energy" className="w-full border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </label>
        <button data-testid="prospect-search-btn" onClick={doSearch} disabled={search.isPending}
          className="md:col-span-2 bg-[#002FA7] text-white py-2.5 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2">
          <MagnifyingGlass size={18} weight="bold" /> {search.isPending ? "Searching…" : "Search"}
        </button>
      </div>

      {search.isPending && (
        <p className="text-[#4B5563] mt-8">Scanning businesses within radius (this can take a few seconds)…</p>
      )}

      {result && !search.isPending && (
        <>
          {result.nearest_airports?.length > 0 && (
            <div className="border border-[#E5E7EB] bg-[#F3F4F6] p-4 mt-8" data-testid="airports-panel">
              <div className="flex items-center gap-2 mb-2">
                <AirplaneTilt size={16} weight="bold" color="#002FA7" />
                <span className="overline text-[#4B5563]">Nearest airports — crews fly in here</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.nearest_airports.map((a) => (
                  <span key={a.code} className="bg-white border border-[#E5E7EB] px-3 py-1.5 text-sm">
                    <b className="text-[#002FA7]">{a.code}</b> {a.name} · <span className="font-mono text-[#4B5563]">{a.distance_miles} mi</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mt-6 mb-4">
            <span className="text-sm text-[#4B5563]" data-testid="prospect-count">
              {companies.length} companies within {result.radius} mi of <b>{result.hotel.name}</b>
              {mode === "crew" ? " (sales leads)" : ""}
            </span>
            {companies.length > 0 && (
              <button data-testid="export-csv-btn" onClick={exportCsv}
                className="inline-flex items-center gap-2 border border-[#111827] px-4 py-2 text-sm font-semibold hover:bg-[#111827] hover:text-white transition-colors active:scale-[0.98]">
                <DownloadSimple size={16} weight="bold" /> Export CSV
              </button>
            )}
          </div>
          {companies.length === 0 ? (
            <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563]">
              No companies found. Try a larger radius.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prospect-results">
              {companies.map((c, i) => (
                <div key={c.name + i} className="rb-card p-5 flex flex-col" data-testid={`prospect-card-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <Buildings size={22} weight="duotone" color="#002FA7" />
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 ${c.source === "platform" ? "bg-[#D1FAE5] text-[#047857]" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                      {c.source === "platform" ? "Platform" : "OSM"}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-[#111827] mt-2 leading-tight">{c.name}</h3>
                  <div className="flex items-center gap-1 text-xs text-[#4B5563] mt-1">
                    <MapPin size={12} weight="fill" /> {c.distance_miles} mi
                    {c.industry && <span className="ml-1 bg-[#F3F4F6] px-1.5 py-0.5">{c.industry}</span>}
                  </div>
                  <div className="space-y-1 mt-3 text-sm flex-1">
                    {c.address && <div className="text-[#4B5563] text-xs">{c.address}</div>}
                    {c.phone && <div className="flex items-center gap-1.5 text-[#111827]"><Phone size={13} weight="fill" color="#002FA7" /> {c.phone}</div>}
                    {c.email && <div className="flex items-center gap-1.5 text-[#111827] truncate"><EnvelopeSimple size={13} weight="fill" color="#002FA7" /> {c.email}</div>}
                    {c.website && <a href={`https://${c.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#002FA7] hover:underline truncate"><Globe size={13} weight="fill" /> {c.website}</a>}
                    {!c.phone && !c.email && <div className="text-xs text-[#9CA3AF]">No public contact info</div>}
                  </div>
                  <button data-testid={`create-lead-${i}`} disabled={added[c.name] || createLead.isPending}
                    onClick={() => createLead.mutate(c)}
                    className={`mt-4 py-2 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-[0.98] ${added[c.name] ? "bg-[#D1FAE5] text-[#047857]" : "bg-[#111827] text-white hover:bg-[#002FA7]"}`}>
                    {added[c.name] ? <><Check size={16} weight="bold" /> Added to pipeline</> : <><Plus size={16} weight="bold" /> Create lead</>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
